import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Form, Input, Button, Upload, Avatar, Spin, message, Typography } from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import type { RcFile } from 'antd/es/upload'

import { useTelegramAccountBranding } from '../../hooks/useTelegramAccountBranding'
import './TelegramAccountBrandingPage.styles.css'

const MAX_AVATAR_SIZE_BYTES = 5 * 1024 * 1024
const ALLOWED_AVATAR_TYPES = ['image/jpeg', 'image/png']

type TTelegramAccountBrandingPageProps = {
  telegramAccountUUID: string
}

const TelegramAccountBrandingPage: React.FC<TTelegramAccountBrandingPageProps> = ({ telegramAccountUUID }) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const cacheWarningShown = useRef(false)

  const { branding, loading, saving, error, save } = useTelegramAccountBranding(telegramAccountUUID)

  useEffect(() => {
    if (branding) {
      form.setFieldsValue({
        firstName: branding.firstName,
        lastName: branding.lastName,
        about: branding.about,
      })
    }
  }, [branding, form])

  useEffect(() => {
    if (error) {
      message.error(t(error, { ns: 'errors' }))
    }
  }, [error, t])

  useEffect(() => {
    if (branding?.loadedFromCache && !cacheWarningShown.current) {
      cacheWarningShown.current = true
      message.warning(t('tg-branding-loaded-from-cache', { ns: 'errors' }))
    }
  }, [branding, t])

  const validateAvatar = (file: RcFile): boolean => {
    if (file.size > MAX_AVATAR_SIZE_BYTES) {
      message.error(t('tg-branding-avatar-too-large', { ns: 'errors' }))
      return false
    }
    if (!ALLOWED_AVATAR_TYPES.includes(file.type)) {
      message.error(t('tg-branding-avatar-unsupported-type', { ns: 'errors' }))
      return false
    }
    setSelectedFile(file)
    return false
  }

  const handleFinish = async (values: { firstName: string; lastName?: string; about?: string }) => {
    const ok = await save({
      firstName: values.firstName,
      lastName: values.lastName || '',
      about: values.about || '',
      avatar: selectedFile,
    })
    if (ok) {
      message.success(t('tg-branding-saved', { ns: 'messages' }))
      navigate('/telegram_accounts')
    }
  }

  const handleCancel = () => {
    navigate('/telegram_accounts')
  }

  if (loading) {
    return (
      <div className="telegram-account-branding-page__loading">
        <Spin size="large" />
      </div>
    )
  }

  const avatarPreviewUrl = selectedFile ? URL.createObjectURL(selectedFile) : (branding?.avatarUrl || undefined)

  return (
    <div className="telegram-account-branding-page">
      <Typography.Title level={3}>{t('telegram-branding-title', { ns: 'standard' })}</Typography.Title>

      <Form form={form} layout="vertical" onFinish={handleFinish} className="telegram-account-branding-page__form">
        <Form.Item label={t('telegram-branding-avatar-label', { ns: 'standard' })}>
          <div className="telegram-account-branding-page__avatar-row">
            <Avatar size={120} src={avatarPreviewUrl} />
            <div className="telegram-account-branding-page__avatar-upload">
              <Upload accept="image/jpeg,image/png" maxCount={1} showUploadList={false} beforeUpload={validateAvatar}>
                <Button icon={<UploadOutlined />}>{t('telegram-branding-upload-avatar', { ns: 'standard' })}</Button>
              </Upload>
              <Typography.Text type="secondary" className="telegram-account-branding-page__avatar-hint">
                {t('telegram-branding-avatar-hint', { ns: 'standard' })}
              </Typography.Text>
            </div>
          </div>
        </Form.Item>

        <Form.Item
          name="firstName"
          label={t('telegram-branding-first-name-label', { ns: 'standard' })}
          rules={[{ required: true, message: t('tg-branding-first-name-empty', { ns: 'errors' }) }]}
        >
          <Input maxLength={64} />
        </Form.Item>

        <Form.Item name="lastName" label={t('telegram-branding-last-name-label', { ns: 'standard' })}>
          <Input maxLength={64} />
        </Form.Item>

        <Form.Item name="about" label={t('telegram-branding-about-label', { ns: 'standard' })}>
          <Input.TextArea
            rows={3}
            maxLength={70}
            showCount
            placeholder={t('telegram-branding-about-placeholder', { ns: 'standard' })}
          />
        </Form.Item>

        <Form.Item>
          <div className="telegram-account-branding-page__actions">
            <Button onClick={handleCancel} disabled={saving}>
              {t('cancel', { ns: 'buttons' })}
            </Button>
            <Button type="primary" htmlType="submit" loading={saving}>
              {t('save', { ns: 'buttons' })}
            </Button>
          </div>
        </Form.Item>
      </Form>
    </div>
  )
}

export default TelegramAccountBrandingPage
